import React, { useState } from 'react';
import { Button, Tooltip } from 'antd';
import styled from 'styled-components';

import { ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

import { DisplaySuggestion } from '../candidate-ai/candidateAiSuggestions.types';
import { CandidateAiReviewAction } from '../candidate-ai/useCandidateAiReviewActions';

// Above this length the suggested value is clamped; jsdom cannot measure line boxes, so length decides.
const CLAMP_THRESHOLD = 180;

const SuggestionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(110px, 160px) minmax(0, 1fr) auto;
  gap: 8px 16px;
  align-items: start;
  padding: 12px 0;
  border-top: 1px solid #e5eaf0;

  .cv-suggestion-label {
    font-weight: 500;
    font-size: 15px;
    color: #18283b;
  }
  .cv-suggestion-values {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    min-width: 0;
  }
  .cv-suggestion-current {
    color: #64748b;
    font-size: 14px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .cv-suggestion-arrow {
    color: #94a3b8;
    font-size: 11px;
  }
  /* Short values stay on the current value's line; long ones wrap below it. */
  .cv-suggestion-suggested {
    flex: 1 1 auto;
    min-width: 0;
  }
  .cv-suggestion-current:empty + .cv-suggestion-arrow {
    display: none;
  }
  .cv-suggestion-value {
    font-weight: 400;
    font-size: 15px;
    line-height: 1.6;
    color: #18283b;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .cv-suggestion-clamped {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .cv-suggestion-toggle {
    padding: 0;
    height: auto;
    font-size: 14px;
  }

  @media (max-width: 576px) {
    grid-template-columns: minmax(0, 1fr) auto;
    .cv-suggestion-values {
      grid-column: 1 / -1;
      grid-row: 2;
    }
  }
`;

const iconToneColors = { accept: '#166534', reject: '#b42318', neutral: '#526174' };
const iconToneHover = {
  accept: 'rgba(34, 197, 94, 0.12)',
  reject: 'rgba(240, 68, 56, 0.1)',
  neutral: 'rgba(15, 23, 42, 0.06)',
};

export const SuggestionIconButton = styled(Button)<{ $tone: 'accept' | 'reject' | 'neutral' }>`
  &.ant-btn {
    width: 28px;
    min-width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 6px;
    box-shadow: none;
    color: ${({ $tone }) => iconToneColors[$tone]};
  }

  &.ant-btn:not(:disabled):hover {
    background: ${({ $tone }) => iconToneHover[$tone]} !important;
  }
`;

const RowActions = styled.div`
  display: inline-flex;
  gap: 4px;
`;

const ClampedValue = ({ value }: { value: string }) => {
  const [expanded, setExpanded] = useState(false);
  const clampable = value.length > CLAMP_THRESHOLD;

  return (
    <div className="cv-suggestion-suggested">
      <div className={`cv-suggestion-value${clampable && !expanded ? ' cv-suggestion-clamped' : ''}`}>{value}</div>
      {clampable && (
        <Button type="link" className="cv-suggestion-toggle" onClick={() => setExpanded((current) => !current)}>
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  );
};

type SuggestionValueRowProps = {
  label: string;
  currentValue: string;
  suggestedValue: string;
  actionsDisabled: boolean;
  acceptLoading?: boolean;
  rejectLoading?: boolean;
  onAccept: () => void;
  onReject?: () => void;
};

export const SuggestionValueRow: React.FC<SuggestionValueRowProps> = ({
  label,
  currentValue,
  suggestedValue,
  actionsDisabled,
  acceptLoading,
  rejectLoading,
  onAccept,
  onReject,
}) => (
  <SuggestionRow role="group" aria-label={label}>
    <div className="cv-suggestion-label">{label}</div>
    <div className="cv-suggestion-values">
      <span className="cv-suggestion-current">{currentValue}</span>
      <ArrowRightOutlined className="cv-suggestion-arrow" aria-label="changes to" />
      <ClampedValue value={suggestedValue} />
    </div>
    <RowActions>
      <Tooltip title="Accept">
        <SuggestionIconButton
          $tone="accept"
          type="text"
          aria-label={`Accept ${label}`}
          icon={<CheckOutlined />}
          loading={acceptLoading}
          disabled={actionsDisabled}
          onClick={onAccept}
        />
      </Tooltip>
      {onReject && (
        <Tooltip title="Reject">
          <SuggestionIconButton
            $tone="reject"
            type="text"
            aria-label={`Reject ${label}`}
            icon={<CloseOutlined />}
            loading={rejectLoading}
            disabled={actionsDisabled}
            onClick={onReject}
          />
        </Tooltip>
      )}
    </RowActions>
  </SuggestionRow>
);

// The review state every suggestion control needs, passed through rows and list sections.
export type SuggestionReviewProps = {
  actionsDisabled: boolean;
  isReviewLoading: boolean;
  activeReviewKey: string | null;
  activeReviewAction: CandidateAiReviewAction | null;
  onReview: (actions: CandidateAiReviewAction[], key: string) => void;
};

type CandidateAiScalarRowsProps = SuggestionReviewProps & { rows: DisplaySuggestion[] };

export const CandidateAiScalarRows: React.FC<CandidateAiScalarRowsProps> = ({
  rows,
  actionsDisabled,
  isReviewLoading,
  activeReviewAction,
  onReview,
}) => (
  <>
    {rows.map((row) => {
      const rowBusy = isReviewLoading && activeReviewAction?.field === row.field;

      return (
        <SuggestionValueRow
          key={row.field}
          label={row.label}
          currentValue={row.canonicalValue}
          suggestedValue={row.suggestedValue}
          actionsDisabled={actionsDisabled}
          acceptLoading={rowBusy && activeReviewAction?.type === 'ACCEPT_SCALAR'}
          rejectLoading={rowBusy && activeReviewAction?.type === 'REJECT_SCALAR'}
          onAccept={() => onReview([{ field: row.field, type: 'ACCEPT_SCALAR' }], `scalar:${row.field}:accept`)}
          onReject={
            row.status === 'PENDING'
              ? () => onReview([{ field: row.field, type: 'REJECT_SCALAR' }], `scalar:${row.field}:reject`)
              : undefined
          }
        />
      );
    })}
  </>
);
